/**
 * IneligibleReasonsPanel — Shows failing conditions from the best-match eligibility path.
 * Each failing param shows: label, what's required, what the loan actually has.
 * Sorted: adjustable (yellow) first, fixed (red) second.
 */
import { Box, Chip, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { filterExplicitRulesForDisplay, filterFailingParamsForDisplay } from '../../../utils/ineligibleReasons';

const IneligibleReasonsPanel = ({ analysis }) => {
  const failingParams = filterFailingParamsForDisplay(analysis?.failingParams);
  const explicitRules = filterExplicitRulesForDisplay(analysis?.explicitRules);
  const { bestPath, allPathScores = [] } = analysis || {};
  if (!failingParams.length && !explicitRules.length) return null;

  const adjustable = failingParams.filter(p => p.changeable);
  const fixed = failingParams.filter(p => !p.changeable);

  const formatActual = (val) => {
    if (val === true) return 'Yes';
    if (val === false) return 'No';
    if (typeof val === 'number') return val.toLocaleString();
    return String(val);
  };

  const renderParam = (fp, i) => {
    const color = fp.changeable ? 'warning.main' : 'error.main';
    return (
      <Box
        key={`${fp.param}-${i}`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderLeft: 3, borderColor: color, pl: 1, mb: 0.5 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            <Typography variant="caption" fontWeight={700} sx={{ color }}>{fp.label}</Typography>
            {fp.changeable && (
              <Chip label="adjustable" size="small" sx={(theme) => ({ height: 16, fontSize: '0.6rem', bgcolor: theme.vars.palette.badges.warning.bg, color: theme.vars.palette.badges.warning.color })} />
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
            <Typography variant="caption" color="text.secondary">
              Required: <strong>{fp.requiredDisplay}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Yours: <Typography component="strong" variant="caption" color="error">{formatActual(fp.actualValue)}</Typography>
            </Typography>
          </Stack>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ fontSize: '0.8rem' }}>
      {/* Summary line */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
        {adjustable.length > 0 && (
          <Chip icon={<EditIcon sx={{ fontSize: 14 }} />} label={`${adjustable.length} adjustable`} size="small" sx={(theme) => ({ height: 20, fontSize: '0.65rem', bgcolor: theme.vars.palette.badges.warning.bg, color: theme.vars.palette.badges.warning.color })} />
        )}
        {fixed.length > 0 && (
          <Chip icon={<LockIcon sx={{ fontSize: 14 }} />} label={`${fixed.length} fixed`} size="small" sx={(theme) => ({ height: 20, fontSize: '0.65rem', bgcolor: theme.vars.palette.badges.danger.bg, color: theme.vars.palette.badges.danger.color })} />
        )}
        {explicitRules.length > 0 && (
          <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />} label={`${explicitRules.length} explicit`} size="small" sx={(theme) => ({ height: 20, fontSize: '0.65rem', bgcolor: theme.vars.palette.badges.info.bg, color: theme.vars.palette.badges.info.color })} />
        )}
        {bestPath && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            Best of {allPathScores.length} paths ({bestPath.failCount} fail / {bestPath.totalConditions} conditions)
          </Typography>
        )}
      </Stack>

      {/* Explicit overlay rules */}
      {explicitRules.map((rule, i) => (
        <Box
          key={`explicit-${i}`}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderLeft: 3, borderColor: 'info.main', pl: 1, mb: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary">{rule}</Typography>
        </Box>
      ))}

      {/* Adjustable params first, then fixed */}
      {adjustable.map((fp, i) => renderParam(fp, i))}
      {fixed.map((fp, i) => renderParam(fp, i))}
    </Box>
  );
};

export default IneligibleReasonsPanel;
